import {
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  useId,
} from 'react';
import { clsx } from 'clsx';
import styles from './TextField.module.css';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string;
  leading?: ReactNode;
}

export function TextField({
  label,
  hint,
  error,
  leading,
  id: suppliedId,
  className,
  ...props
}: TextFieldProps) {
  const generatedId = useId();
  const id = suppliedId ?? generatedId;
  const descriptionId = `${id}-description`;

  return (
    <label className={clsx(styles.root, className)} htmlFor={id}>
      <span className={styles.label}>{label}</span>
      <span className={styles.control}>
        {leading && <span className={styles.leading}>{leading}</span>}
        <input
          id={id}
          className={styles.input}
          aria-invalid={Boolean(error)}
          aria-describedby={hint || error ? descriptionId : undefined}
          {...props}
        />
      </span>
      {(error || hint) && (
        <span
          id={descriptionId}
          className={clsx(styles.hint, error && styles.hintError)}
        >
          {error ?? hint}
        </span>
      )}
    </label>
  );
}

/** @deprecated Use TextField */
export const Field = TextField;

export function SelectField({
  label,
  children,
  className,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  return (
    <label className={clsx(styles.root, className)}>
      <span className={styles.label}>{label}</span>
      <span className={styles.control}>
        <select className={styles.select} {...props}>
          {children}
        </select>
      </span>
    </label>
  );
}
